import React from 'react'
import { Box, Flex, HStack, Image, Text, VStack, chakra } from '@chakra-ui/react'
import { LuCreditCard, LuGift, LuLandmark, LuReceipt, LuWallet } from 'react-icons/lu'
import { formatCurrency, type DonationEvent } from '@kuow/core'
import {
  eventMethodLabel, eventTitle, formatActivityDate, isGiftEvent, statusBadgeFor,
} from './activityFormat'

export interface ActivityTimelineItemProps {
  event: DonationEvent
  isLast: boolean
  onViewReceipt: (event: DonationEvent) => void
}

function iconFor(event: DonationEvent) {
  if (isGiftEvent(event)) return LuGift
  switch (event.account_type) {
    case 'bank_account':
      return LuLandmark
    case 'paypal':
      return LuWallet
    default:
      return LuCreditCard
  }
}

export const ActivityTimelineItem: React.FC<ActivityTimelineItemProps> = ({ event, isLast, onViewReceipt }) => {
  const Icon = iconFor(event)
  const gift = isGiftEvent(event)
  const badge = gift ? null : statusBadgeFor(event.status)
  const method = gift ? null : eventMethodLabel(event)
  const giftImage = event.gift_image?.cdn_url ?? null

  return (
    <HStack align="stretch" gap={4}>
      <VStack gap={0} align="center" flexShrink={0}>
        <Flex align="center" justify="center" boxSize="36px" borderRadius="full" bg="gray.solid" color="gray.contrast" flexShrink={0}>
          <Icon size={14} aria-hidden />
        </Flex>
        {!isLast ? <Box flex="1" w="1px" bg="bg.muted" my={1} /> : null}
      </VStack>
      <VStack align="stretch" gap={2} flex="1" minW={0} pb={isLast ? 0 : 6}>
        <Flex gap={1.5} wrap="wrap" rowGap={1.5} align="center">
          <Text fontSize="sm" whiteSpace="nowrap">{eventTitle(event)}</Text>
          {badge ? (
            <Box bg={badge.bg} color={badge.fg} px={1.5} py={0.5} borderRadius="l2" fontSize="xs" lineHeight="1.3" whiteSpace="nowrap">
              {badge.label}
            </Box>
          ) : null}
          {!gift && event.detail?.type === 'receipt' ? (
            <chakra.button
              type="button"
              onClick={() => onViewReceipt(event)}
              display="inline-flex"
              alignItems="center"
              gap={1}
              bg="gray.subtle"
              color="gray.fg"
              px={1.5}
              py={0.5}
              borderRadius="l2"
              fontSize="xs"
              lineHeight="1.3"
              whiteSpace="nowrap"
              cursor="pointer"
            >
              <Text>View receipt</Text>
              <LuReceipt size={12} aria-hidden />
            </chakra.button>
          ) : null}
        </Flex>
        {gift ? (
          <>
            <Text fontSize="sm" color="fg.muted">
              <Text as="span" fontWeight="semibold">{formatActivityDate(event.occurred_at)}</Text>
            </Text>
            {giftImage ? (
              <Box bg="bg.panel" borderRadius="l3" p={4} borderWidth="1px" borderColor="bg.muted">
                <Image src={giftImage} alt={event.gift_name ?? ''} maxH="200px" mx="auto" />
                {event.gift_name ? <Text fontSize="sm" fontWeight="semibold" mt={3}>{event.gift_name}</Text> : null}
              </Box>
            ) : event.gift_name ? (
              <Text fontSize="sm" fontWeight="semibold">{event.gift_name}</Text>
            ) : null}
          </>
        ) : (
          <Text fontSize="sm" color="fg.muted">
            <Text as="span" fontWeight="semibold">{formatActivityDate(event.occurred_at)}</Text>
            {' - '}
            <Text as="span" fontWeight="semibold">{formatCurrency(event.amount ?? 0, true)}</Text>
            {method ? <> {' - '}{method}</> : null}
          </Text>
        )}
      </VStack>
    </HStack>
  )
}
